import { useState } from "react";
import { X, Trash2, Copy, Heart, History, Star } from "lucide-react";
import { toast } from "sonner";

export default function HistoryFavoritesPanel({
  onClose,
  history,
  favorites,
  onDeleteHistory,
  onDeleteFavorite,
  onAddFavorite,
}) {
  const [tab, setTab] = useState("history");

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié 📋");
    } catch {
      toast.error("Copie impossible");
    }
  };

  return (
    <div
      data-testid="side-panel"
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
    >
      <button
        aria-label="Fermer"
        data-testid="panel-backdrop"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <aside className="relative w-full sm:max-w-md bg-[#FFFDF9] h-full overflow-y-auto border-l-2 border-[#0A0A0A]">
        <div className="sticky top-0 bg-[#FFFDF9] border-b-2 border-[#0A0A0A] p-4 flex items-center justify-between z-10">
          <h2 className="font-display font-black text-xl">Ma bibliothèque</h2>
          <button
            data-testid="close-panel-button"
            onClick={onClose}
            className="nb-border bg-white p-2 hover:bg-[#FF4D85]"
            aria-label="Fermer le panneau"
          >
            <X size={18} strokeWidth={3} />
          </button>
        </div>

        <div className="p-4 flex gap-2 border-b-2 border-[#0A0A0A]">
          <button
            data-testid="tab-history"
            onClick={() => setTab("history")}
            className={
              "flex-1 nb-border px-3 py-2 font-black text-sm flex items-center justify-center gap-1.5 " +
              (tab === "history" ? "bg-[#FF8A00]" : "bg-white hover:bg-[#F3F4F6]")
            }
          >
            <History size={14} strokeWidth={2.5} /> Historique ({history.length})
          </button>
          <button
            data-testid="tab-favorites"
            onClick={() => setTab("favorites")}
            className={
              "flex-1 nb-border px-3 py-2 font-black text-sm flex items-center justify-center gap-1.5 " +
              (tab === "favorites" ? "bg-[#FF4D85]" : "bg-white hover:bg-[#F3F4F6]")
            }
          >
            <Star size={14} strokeWidth={2.5} /> Favoris ({favorites.length})
          </button>
        </div>

        <div className="p-4 space-y-3">
          {tab === "history" && (
            <>
              {history.length === 0 && (
                <p className="text-sm font-bold text-[#0A0A0A]/60 text-center py-8">
                  Aucune réplique générée pour l'instant.
                </p>
              )}
              {history.map((h) => (
                <article
                  key={h.id}
                  data-testid={`history-item-${h.id}`}
                  className="nb-border bg-white p-3"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-xs font-black uppercase opacity-70">
                      {h.tone} · {new Date(h.created_at).toLocaleDateString("fr-FR")}
                    </p>
                    <button
                      data-testid={`del-history-${h.id}`}
                      onClick={() => onDeleteHistory(h.id)}
                      className="nb-border bg-white px-1.5 py-1 hover:bg-[#FF3B30] hover:text-white"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                  <p className="text-xs font-bold italic mb-2 line-clamp-2">
                    « {h.advice} »
                  </p>
                  <div className="space-y-2">
                    {h.comebacks?.map((c, i) => (
                      <div key={i} className="nb-border bg-[#FFFDF9] p-2">
                        <p className="text-sm font-bold mb-1">→ {c.text}</p>
                        <div className="flex gap-1">
                          <button
                            onClick={() => copy(c.text)}
                            className="nb-border bg-white px-2 py-0.5 text-xs font-bold hover:bg-[#FCD34D]"
                          >
                            <Copy size={10} className="inline" /> Copier
                          </button>
                          <button
                            onClick={() => onAddFavorite(c.text, h.advice, h.tone)}
                            className="nb-border bg-white px-2 py-0.5 text-xs font-bold hover:bg-[#FF4D85]"
                          >
                            <Heart size={10} className="inline" /> Favori
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </>
          )}

          {tab === "favorites" && (
            <>
              {favorites.length === 0 && (
                <p className="text-sm font-bold text-[#0A0A0A]/60 text-center py-8">
                  Pas encore de favori. Clique sur ❤️ pour en ajouter.
                </p>
              )}
              {favorites.map((f) => (
                <article
                  key={f.id}
                  data-testid={`fav-item-${f.id}`}
                  className="nb-border bg-[#FFD6E8] p-3"
                >
                  <p className="font-bold text-sm mb-2">« {f.text} »</p>
                  {f.advice && (
                    <p className="text-xs italic opacity-70 mb-2">
                      Réponse à : {f.advice.slice(0, 60)}
                      {f.advice.length > 60 ? "…" : ""}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button
                      data-testid={`fav-copy-${f.id}`}
                      onClick={() => copy(f.text)}
                      className="nb-border bg-white px-2 py-1 text-xs font-bold hover:bg-[#FCD34D]"
                    >
                      <Copy size={12} className="inline mr-1" /> Copier
                    </button>
                    <button
                      data-testid={`del-fav-${f.id}`}
                      onClick={() => onDeleteFavorite(f.id)}
                      className="nb-border bg-white px-2 py-1 text-xs font-bold hover:bg-[#FF3B30] hover:text-white"
                    >
                      <Trash2 size={12} className="inline mr-1" /> Supprimer
                    </button>
                  </div>
                </article>
              ))}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
